import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Calendar,
  FileText,
  MapPin,
  AlertCircle,
  Check,
  Clock,
  Pin,
  Loader2,
  XCircle,
  Zap,
  Target,
  Info,
  ChevronDown,
  ChevronUp,
  Link2,
  Building2,
  Scale,
  Square,
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
  EyeOff,
  ListChecks,
} from "lucide-react";
import PdfSourceViewer from "./PdfSourceViewer";
import apiClient from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

export interface EvidenceRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface InsightEvidenceItem {
  document_id: number;
  page_number: number;
  quote: string;
  highlightable: boolean;
  rects: EvidenceRect[];
}

interface PulseItem {
  id: string;
  insightId: number;
  title: string;
  source: string;
  sourceType: "municipal" | "regional" | "provincial";
  category: string;
  date: string;
  whatChanged: string;
  whoWhereAffects: string;
  impactOnYou?: string;
  suggestedAction?: string;
  evidenceUrl?: string;
  status: string;
  confidence: number;
  isRelevant?: boolean;
  // New personalized fields
  tldrShort?: string;
  whatChangedBullets?: string[];
  impact?: string; // high, medium, low (Impact Status rubric)
  timeSensitivity?: string;
  actionBullets?: string[];
  // Key dates from insight
  itemNumber?: string;
  publishedDate?: string;
  meetingDate?: string;
  timeAnchorType?: string;
  timeAnchorDate?: string;
  timeAnchorLabel?: string;
  decisionStatus?: string;
  // PDF source citations (Phase 5)
  documentId?: number;
  evidence?: InsightEvidenceItem[];
  /** True when linked insight has meaningful_change_check === 'Needs Review' */
  needsReview?: boolean;
  /** True when at least one correction was submitted for this pulse */
  hasCorrection?: boolean;
  /** True when user hid the pulse after correction (excluded from dashboard) */
  hiddenAfterCorrection?: boolean;
  /** Full URL to municipality meeting agenda (eSCRIBE Meeting.aspx?Id=...) */
  agendaUrl?: string | null;
  /** Meeting start from public_events.event_start (ISO string) for "Meeting: Mar 20 2025, 2:30 PM" */
  eventStart?: string | null;
}

interface PulseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  pulse: PulseItem | null;
  isPinned: boolean;
  onTogglePin: () => void;
  onDismiss?: () => void;
  /** Called after user clicks Hide (pulse hidden from dashboard). Use to invalidate queries and close. */
  onPulseHidden?: () => void;
  /** Called when user confirms Unpin in the modal. Use this to actually unpin (onTogglePin may rely on stale is_pinned). */
  onUnpin?: (insightId: number) => void;
  isLoading?: boolean;
}

// Impact badge component
function ImpactBadge({ impact, timeSensitivity }: { 
  impact?: string; // high, medium, low
  timeSensitivity?: string;
}) {
  const getImpactStyle = () => {
    if (impact === 'high') return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    if (impact === 'medium') return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
    return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
  };

  const getImpactLabel = () => {
    if (impact === 'high') return 'High Impact';
    if (impact === 'medium') return 'Medium Impact';
    return 'Low Impact';
  };

  const getImpactDescription = () => {
    if (impact === 'high') return 'Structural change or time-critical action window (within 30 days). Affects feasibility, cost, timeline, or constraints.';
    if (impact === 'medium') return 'Substantive update but not time-critical, or applies to narrower geography/project types.';
    return 'Low materiality - minimal new information for planning decisions.';
  };

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-2">
        {impact && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className={cn("gap-1.5 border", getImpactStyle())}>
                <Zap className="h-3 w-3" />
                {getImpactLabel()}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <div className="space-y-1 text-xs">
                <p><strong>Impact:</strong> {getImpactDescription()}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
        {timeSensitivity && timeSensitivity !== 'low' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={cn(
                "gap-1.5",
                timeSensitivity === 'high' 
                  ? "border-red-300 text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400" 
                  : "border-amber-300 text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
              )}>
                <Clock className="h-3 w-3" />
                {timeSensitivity === 'high' ? 'Urgent' : 'Time-Sensitive'}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">
                {timeSensitivity === 'high' 
                  ? 'Action required within days or weeks' 
                  : 'Action recommended within the next month'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

// Status badge with hover explanation
function StatusBadge({ status, decisionStatus }: { status?: string; decisionStatus?: string }) {
  const [showExplanation, setShowExplanation] = useState(false);
  const rawStatus = decisionStatus || status;
  
  // Format status: replace underscores with spaces and capitalize each word
  const formatStatus = (s?: string) => {
    if (!s) return 'Unknown';
    return s
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  const displayStatus = formatStatus(rawStatus);
  
  const getStatusStyle = () => {
    const s = rawStatus?.toLowerCase();
    if (s?.includes('adopt') || s?.includes('approv') || s?.includes('force')) 
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (s?.includes('consult') || s?.includes('review')) 
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    if (s?.includes('draft') || s?.includes('discuss')) 
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300';
  };

  const getStatusExplanation = () => {
    const s = rawStatus?.toLowerCase();
    if (s?.includes('force')) return 'This policy is now in effect and legally binding.';
    if (s?.includes('adopt')) return 'Officially adopted by council, pending implementation date.';
    if (s?.includes('approv')) return 'Approved by the governing authority.';
    if (s?.includes('consult')) return 'Open for public input and feedback. You may be able to submit comments.';
    if (s?.includes('review')) return 'Currently under review by decision-makers. No final decision yet.';
    if (s?.includes('draft')) return 'Preliminary version that may change before final adoption.';
    if (s?.includes('discuss')) return 'Being discussed at committee or council level. No decision made yet.';
    return 'Current stage in the decision process.';
  };

  return (
    <div className="relative inline-block">
      <Badge 
        className={cn("gap-1.5 cursor-help", getStatusStyle())}
        onMouseEnter={() => setShowExplanation(true)}
        onMouseLeave={() => setShowExplanation(false)}
        title={getStatusExplanation()}
      >
        <Scale className="h-3 w-3" />
        {displayStatus}
      </Badge>
      {showExplanation && (
        <div className="absolute z-[200] left-0 top-full mt-2 p-2 bg-neutral-900 text-white text-xs rounded-lg shadow-xl max-w-xs whitespace-normal">
          {getStatusExplanation()}
        </div>
      )}
    </div>
  );
}

export default function PulseDetailModal({
  isOpen,
  onClose,
  onBack,
  pulse,
  isPinned,
  onTogglePin,
  onDismiss,
  onPulseHidden,
  onUnpin,
  isLoading = false,
}: PulseDetailModalProps) {
  const { toast } = useToast();
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [showUnpinConfirm, setShowUnpinConfirm] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionExplanation, setCorrectionExplanation] = useState("");
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [hideSubmitting, setHideSubmitting] = useState(false);
  const [hasViewedPulse, setHasViewedPulse] = useState(false);
  const [dismissAction, setDismissAction] = useState<'back' | 'close'>('close');
  const [showFullWhatChanged, setShowFullWhatChanged] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [checkedActions, setCheckedActions] = useState<Set<number>>(new Set());
  // View in PDF drawer (Phase 5)
  const [showPdfDrawer, setShowPdfDrawer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  /** True when the PDF shown is the item-specific PDF (highlights don't apply). */
  const [isViewingItemPdf, setIsViewingItemPdf] = useState(false);
  const [pagesData, setPagesData] = useState<{ page_number: number; page_width_pts: number; page_height_pts: number }[]>([]);
  const [numPdfPages, setNumPdfPages] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  /** Current PDF page (1-based); user changes via Prev/Next at top. */
  const [drawerPdfPage, setDrawerPdfPage] = useState<number | null>(null);

  // Load checked actions from localStorage
  const getCheckedActionsKey = useCallback(() => {
    return pulse ? `pulse_actions_${pulse.id}` : null;
  }, [pulse]);

  useEffect(() => {
    // Reset expand states when pulse changes
    setShowFullSummary(false);
    setShowFullWhatChanged(false);
    
    if (pulse) {
      const key = `pulse_actions_${pulse.id}`;
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          setCheckedActions(new Set(JSON.parse(stored)));
        } else {
          setCheckedActions(new Set());
        }
      } catch {
        setCheckedActions(new Set());
      }
    }
  }, [pulse?.id]);

  const toggleActionChecked = (index: number) => {
    const newChecked = new Set(checkedActions);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedActions(newChecked);
    
    // Persist to localStorage
    if (pulse) {
      const key = `pulse_actions_${pulse.id}`;
      try {
        localStorage.setItem(key, JSON.stringify([...newChecked]));
      } catch {
        // Ignore localStorage errors
      }
    }
  };

  // Helper to check if user has already been asked about this pulse
  const getDismissAskedPulses = (): Set<string> => {
    try {
      const stored = localStorage.getItem('dismissAskedPulses');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };

  const markDismissAsked = (pulseId: string) => {
    try {
      const asked = getDismissAskedPulses();
      asked.add(pulseId);
      localStorage.setItem('dismissAskedPulses', JSON.stringify([...asked]));
    } catch {
      // Ignore localStorage errors
    }
  };

  const shouldShowDismissPrompt = (): boolean => {
    if (!pulse) return false;
    const asked = getDismissAskedPulses();
    return !asked.has(pulse.id);
  };

  useEffect(() => {
    if (isOpen && pulse && !isLoading) {
      setHasViewedPulse(true);
    }
    if (!isOpen) {
      setHasViewedPulse(false);
      setShowFullWhatChanged(false);
      setShowPdfDrawer(false);
    }
  }, [isOpen, pulse, isLoading]);

  // When opening PDF drawer: fetch item PDF when available (documentId + itemNumber), else full document PDF
  useEffect(() => {
    if (!showPdfDrawer || !pulse?.documentId) return;
    setPdfLoading(true);
    setPdfError(null);
    let blobUrl: string | null = null;
    (async () => {
      try {
        let blob: Blob;
        let pages: { page_number: number; page_width_pts: number; page_height_pts: number }[];
        const docId = pulse.documentId!;
        const itemNumber = pulse.itemNumber;
        let usedItemPdf = false;
        if (itemNumber) {
          const itemBlob = await apiClient.pulses.getItemPdfBlob(docId, itemNumber);
          if (itemBlob) {
            blob = itemBlob;
            pages = [{ page_number: 1, page_width_pts: 612, page_height_pts: 792 }];
            usedItemPdf = true;
          } else {
            blob = await apiClient.pulses.getDocumentPdfBlob(docId);
            pages = await apiClient.pulses.getDocumentPages(docId);
          }
        } else {
          blob = await apiClient.pulses.getDocumentPdfBlob(docId);
          pages = await apiClient.pulses.getDocumentPages(docId);
        }
        if (blob.size === 0) {
          throw new Error("PDF is empty");
        }
        const pdfBlob = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
        blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        setIsViewingItemPdf(usedItemPdf);
        setPagesData(pages);
        setNumPdfPages(0);
        setDrawerPdfPage(null);
      } catch (e) {
        setPdfError(e instanceof Error ? e.message : "Failed to load PDF");
      } finally {
        setPdfLoading(false);
      }
    })();
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [showPdfDrawer, pulse?.documentId, pulse?.itemNumber]);

  const closePdfDrawer = useCallback(() => {
    setShowPdfDrawer(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setIsViewingItemPdf(false);
    setPagesData([]);
    setNumPdfPages(0);
  }, [pdfBlobUrl]);

  const handleClosePdfDrawerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      closePdfDrawer();
    },
    [closePdfDrawer]
  );

  // Close PDF drawer on Escape so users can always exit even if overlay/X is blocked
  useEffect(() => {
    if (!showPdfDrawer) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        closePdfDrawer();
      }
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [showPdfDrawer, closePdfDrawer]);

  const handleCloseAttempt = () => {
    if (hasViewedPulse && !isPinned && onDismiss && shouldShowDismissPrompt()) {
      setDismissAction('close');
      setShowDismissConfirm(true);
    } else {
      onClose();
    }
  };

  const handleBackAttempt = () => {
    if (hasViewedPulse && !isPinned && onDismiss && shouldShowDismissPrompt()) {
      setDismissAction('back');
      setShowDismissConfirm(true);
    } else {
      onBack();
    }
  };

  const handleConfirmDismiss = () => {
    setShowDismissConfirm(false);
    if (pulse) markDismissAsked(pulse.id);
    onDismiss?.();
    if (dismissAction === 'back') {
      onBack();
    } else {
      onClose();
    }
  };

  const handleCancelDismiss = () => {
    setShowDismissConfirm(false);
    if (pulse) markDismissAsked(pulse.id);
    if (dismissAction === 'back') {
      onBack();
    } else {
      onClose();
    }
  };

  const handleSubmitCorrection = async () => {
    if (!pulse?.insightId || !correctionExplanation.trim()) return;
    setCorrectionSubmitting(true);
    try {
      await apiClient.pulses.submitCorrection(pulse.insightId, correctionExplanation.trim());
      toast({
        title: "Correction recorded",
        description: "Your feedback will be used to improve the model.",
      });
      setShowCorrectionDialog(false);
      setCorrectionExplanation("");
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to submit correction.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const handleHide = async () => {
    if (!pulse?.insightId) return;
    setHideSubmitting(true);
    try {
      await apiClient.pulses.hide(pulse.insightId);
      toast({
        title: "Pulse hidden",
        description: "This pulse has been removed from your dashboard.",
      });
      onPulseHidden?.();
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to hide pulse.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setHideSubmitting(false);
    }
  };

  return (
    <>
    {/* Dismiss Confirmation Dialog */}
    <AlertDialog open={showDismissConfirm} onOpenChange={setShowDismissConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Not relevant to you?</AlertDialogTitle>
          <AlertDialogDescription>
            You viewed this pulse but didn't pin it. Would you like to remove it from your feed as not relevant?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancelDismiss}>
            Keep in feed
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmDismiss}
            className="bg-red-600 hover:bg-red-700"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Remove from feed
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCloseAttempt()}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden border-none shadow-2xl max-h-[90vh] flex flex-col bg-white dark:bg-neutral-900">
        <DialogHeader className="sr-only">
          <DialogTitle>{pulse?.title || "Loading..."}</DialogTitle>
          <DialogDescription>Detailed view of pulse</DialogDescription>
        </DialogHeader>

        {/* Header — Back, category, and Meeting date/time */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex-shrink-0 min-w-0 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackAttempt}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          {pulse && (
            <>
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400 whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                {pulse.category}
              </span>
              {pulse.eventStart && (() => {
                try {
                  const d = new Date(pulse.eventStart);
                  if (Number.isNaN(d.getTime())) return null;
                  // Pipeline stores event_start as naive local time (municipality) but in a UTC column,
                  // so 9:30 AM local is stored as 09:30 UTC. Display using UTC components so it shows 9:30 AM.
                  const mon = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
                  const day = d.getUTCDate();
                  const year = d.getUTCFullYear();
                  const dateStr = `${mon} ${day} ${year}`;
                  let hour = d.getUTCHours();
                  const minute = d.getUTCMinutes();
                  const ampm = hour >= 12 ? "PM" : "AM";
                  hour = hour % 12 || 12;
                  const timeStr = `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
                  return (
                    <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                      Meeting: {dateStr}, {timeStr}
                    </span>
                  );
                } catch {
                  return null;
                }
              })()}
            </>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading || !pulse ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
          <div className="p-6 space-y-6">
            {/* Impact badges + Needs Review / Marked Correction on same row */}
            <div className="flex flex-wrap items-center gap-2">
              <ImpactBadge 
                impact={pulse.impact}
                timeSensitivity={pulse.timeSensitivity}
              />
              {pulse.needsReview && (
                <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-800 dark:text-amber-200 border-amber-500/40 whitespace-nowrap">
                  Needs Review
                </Badge>
              )}
              {pulse.hasCorrection && (
                <Badge variant="secondary" className="text-xs bg-neutral-500/20 text-neutral-700 dark:text-neutral-300 border-neutral-500/40 whitespace-nowrap">
                  Marked Correction
                </Badge>
              )}
            </div>

          {/* Title */}
            <h2 className="text-xl font-bold text-neutral-900 dark:text-white leading-snug">
            {pulse.title}
          </h2>

            {/* Quick Summary Box */}
            {pulse.tldrShort && (
              <div className="rounded-xl p-4 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary mb-1 block">
                      Quick Summary
                    </span>
                    <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                      {pulse.tldrShort}
                    </p>
                    
                    {/* Expanded full summary */}
                    {showFullSummary && pulse.whatChanged && (
                      <p className="mt-3 pt-3 border-t border-primary/20 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        {pulse.whatChanged}
                      </p>
                    )}
                    
                    {/* Read more button */}
                    {pulse.whatChanged && (
                      <button
                        onClick={() => setShowFullSummary(!showFullSummary)}
                        className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        {showFullSummary ? (
                          <>Show less <ChevronUp className="h-4 w-4" /></>
                        ) : (
                          <>Read more <ChevronDown className="h-4 w-4" /></>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Source / Evidence / Key Dates Block */}
            <div className="rounded-xl p-4 bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Source & Key Dates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-500">Jurisdiction:</span>
                    <span className="font-medium text-neutral-700 dark:text-neutral-300">
                      {pulse.source}
                    </span>
                  </div>
                  {pulse.itemNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500">Item:</span>
                      <span className="font-medium text-neutral-700 dark:text-neutral-300">
                        {pulse.itemNumber}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-500">Stage:</span>
                    <StatusBadge status={pulse.status} decisionStatus={pulse.decisionStatus} />
                  </div>
                  {(pulse.timeAnchorLabel || pulse.timeAnchorDate) && (() => {
                    const full = pulse.timeAnchorLabel || pulse.timeAnchorDate || "";
                    const colonSpace = full.indexOf(": ");
                    if (colonSpace >= 0) {
                      const before = full.slice(0, colonSpace + 1); // include colon
                      const after = full.slice(colonSpace + 2).trim(); // after ": "
                      return (
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-500 dark:text-neutral-400">{before}</span>
                          <span className="font-medium text-neutral-700 dark:text-neutral-300">{after}</span>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-neutral-700 dark:text-neutral-300">{full}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
          </div>

            {/* What Changed Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              What Changed
            </h3>
              
              {/* Bullet points if available */}
              {pulse.whatChangedBullets && Array.isArray(pulse.whatChangedBullets) && pulse.whatChangedBullets.length > 0 ? (
                <ul className="space-y-2 ml-1">
                  {pulse.whatChangedBullets
                    .filter((bullet): bullet is string => typeof bullet === 'string' && bullet.trim().length > 0)
                    .map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                        <span className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                          {bullet}
                        </span>
                      </li>
                    ))}
                </ul>
              ) : (
                <div>
                  <p className={cn(
                    "text-neutral-700 dark:text-neutral-300 leading-relaxed",
                    !showFullWhatChanged && pulse.whatChanged && pulse.whatChanged.length > 300 && "line-clamp-4"
                  )}>
                    {pulse.whatChanged}
                  </p>
                  {pulse.whatChanged && pulse.whatChanged.length > 300 && (
                    <button
                      onClick={() => setShowFullWhatChanged(!showFullWhatChanged)}
                      className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      {showFullWhatChanged ? (
                        <>Show less <ChevronUp className="h-4 w-4" /></>
                      ) : (
                        <>Read more <ChevronDown className="h-4 w-4" /></>
                      )}
                    </button>
                  )}
                </div>
              )}
          </div>

            {/* Who/Where It Affects */}
          {pulse.whoWhereAffects && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Who / Where It Affects
              </h3>
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {pulse.whoWhereAffects}
              </p>
            </div>
          )}

            {/* Impact On You - Styled box */}
          {pulse.impactOnYou && (
            <div className={cn(
                "rounded-xl p-4 border",
              pulse.isRelevant === false 
                ? "bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700"
                  : "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800"
              )}>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                Impact On You
              </h3>
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {pulse.impactOnYou}
              </p>
            </div>
          )}

            {/* Suggested Actions - Green styled box with bullets */}
            {((pulse.actionBullets && pulse.actionBullets.length > 0) || pulse.suggestedAction) && pulse.isRelevant !== false && (
              <div className="rounded-xl p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">
                    Suggested Actions
              </h3>
                  {pulse.actionBullets && pulse.actionBullets.length > 0 && (
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded-full",
                      checkedActions.size === pulse.actionBullets.length
                        ? "bg-green-600 text-white"
                        : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
                    )}>
                      {checkedActions.size}/{pulse.actionBullets.length} done
                    </span>
                  )}
                </div>
                {pulse.actionBullets && pulse.actionBullets.length > 0 ? (
                  <ul className="space-y-2">
                    {pulse.actionBullets.map((action, idx) => {
                      const isChecked = checkedActions.has(idx);
                      return (
                        <li 
                          key={idx} 
                          className={cn(
                            "flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-all",
                            isChecked 
                              ? "bg-green-100 dark:bg-green-900/30" 
                              : "hover:bg-green-50 dark:hover:bg-green-900/10"
                          )}
                          onClick={() => toggleActionChecked(idx)}
                        >
                          <div className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all",
                            isChecked 
                              ? "bg-green-600 border-green-600" 
                              : "border-green-400 hover:border-green-500"
                          )}>
                            {isChecked && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <span className={cn(
                            "text-neutral-700 dark:text-neutral-300 transition-all",
                            isChecked && "line-through opacity-70"
                          )}>
                            {action}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {pulse.suggestedAction}
                  </p>
                )}
            </div>
          )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            {pulse && (
              pulse.hasCorrection ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleHide}
                  disabled={hideSubmitting}
                  className="text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  {hideSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <EyeOff className="h-4 w-4 mr-1" />
                  )}
                  Hide
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCorrectionDialog(true)}
                  className="text-neutral-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  <MessageSquarePlus className="h-4 w-4 mr-1" />
                  Mark correction
                </Button>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            {pulse?.agendaUrl && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="gap-1"
              >
                <a href={pulse.agendaUrl} target="_blank" rel="noopener noreferrer">
                  <ListChecks className="h-3 w-3" />
                  View Agenda
                </a>
              </Button>
            )}
            {pulse?.documentId != null && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPdfDrawer(true)}
                className="gap-1"
              >
                <FileText className="h-3 w-3" />
                View Item
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                if (isPinned) {
                  setShowUnpinConfirm(true);
                } else {
                  onTogglePin();
                }
              }}
              className={cn(
                "gap-2 px-4",
                isPinned
                  ? "bg-neutral-200 text-neutral-700 hover:bg-neutral-300 dark:bg-neutral-700 dark:text-neutral-200"
                  : "bg-primary text-white hover:bg-primary/90"
              )}
            >
              <Pin className={cn("h-4 w-4", isPinned && "fill-current")} />
              {isPinned ? "Unpin" : "Pin Pulse"}
            </Button>
          </div>
        </div>

        {/* View in PDF drawer (Phase 5) — inside DialogContent so clicks (X / overlay) don't close the pulse modal */}
        {showPdfDrawer && pulse?.documentId != null && (
          <div className="fixed inset-0 z-[2000] flex" role="dialog" aria-modal="true" aria-label="Source in PDF">
            <button
              type="button"
              className="absolute inset-0 bg-black/40 cursor-pointer border-0 p-0 m-0"
              aria-label="Close PDF viewer"
              onClick={handleClosePdfDrawerClick}
            />
            <div className="relative ml-auto w-full max-w-2xl h-full bg-white dark:bg-neutral-900 shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex-shrink-0">
                <h3 className="font-semibold text-neutral-900 dark:text-white">Source in PDF</h3>
                <Button type="button" variant="ghost" size="icon" aria-label="Close PDF viewer" onClick={handleClosePdfDrawerClick} className="text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 shrink-0">
                  <XCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {pdfLoading && (
                  <div className="flex items-center justify-center gap-2 py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span>Loading PDF...</span>
                  </div>
                )}
                {pdfError && (
                  <div className="space-y-4 py-6">
                    <p className="text-center text-red-600 dark:text-red-400 font-medium">{pdfError}</p>
                    {pulse?.evidence?.[0] && (
                      <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1 rounded-lg bg-neutral-100 dark:bg-neutral-800/50 p-4">
                        <p><strong>Cited:</strong> {pulse.evidence[0].quote}</p>
                        <p className="text-amber-600 dark:text-amber-400">Highlight unavailable — quote could not be matched to exact location.</p>
                      </div>
                    )}
                  </div>
                )}
                {!pdfLoading && !pdfError && pdfBlobUrl && (
                  <>
                    {/* PDF page controls at top: navigate actual PDF pages (count from loaded PDF) */}
                    {(() => {
                      const totalPages = numPdfPages > 0 ? numPdfPages : Math.max(1, pagesData.length);
                      return totalPages > 1 ? (
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setDrawerPdfPage((p) => Math.max(1, (p ?? 1) - 1))
                              }
                              disabled={(drawerPdfPage ?? 1) <= 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Prev
                            </Button>
                            <span className="text-sm text-neutral-500">
                              Page {drawerPdfPage ?? pulse.evidence?.[0]?.page_number ?? 1} of{" "}
                              {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setDrawerPdfPage((p) =>
                                  Math.min(totalPages, (p ?? 1) + 1)
                                )
                              }
                              disabled={(drawerPdfPage ?? pulse.evidence?.[0]?.page_number ?? 1) >= totalPages}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {pulse.evidence && pulse.evidence.length > 0 ? (
                      (() => {
                        const currentPage = drawerPdfPage ?? (pulse.evidence[0].page_number ?? 1);
                        const pageDim = pagesData.find((p) => p.page_number === currentPage);
                        const pageWidthPts = pageDim?.page_width_pts ?? 612;
                        const pageHeightPts = pageDim?.page_height_pts ?? 792;
                        const evidenceOnPage = pulse.evidence.filter(
                          (ev) => (ev.page_number ?? 1) === currentPage
                        );
                        const mergedRects = evidenceOnPage.flatMap((ev) => ev.rects ?? []);
                        const anyHighlightable = evidenceOnPage.some((ev) => ev.highlightable);
                        const evidencePhrases = pulse.evidence?.map((ev) => ev.quote).filter(Boolean) ?? [];
                        const totalPages = numPdfPages > 0 ? numPdfPages : Math.max(1, pagesData.length);
                        return (
                          <>
                            <PdfSourceViewer
                              pdfUrl={pdfBlobUrl}
                              pageNumber={currentPage}
                              pageWidthPts={pageWidthPts}
                              pageHeightPts={pageHeightPts}
                              rects={mergedRects}
                              quotes={evidencePhrases}
                              quote=""
                              highlightable={anyHighlightable}
                              numPages={totalPages}
                              onDocumentLoadSuccess={({ numPages }) => setNumPdfPages(numPages)}
                              onPageChange={(p) => setDrawerPdfPage(p)}
                              showPageControls={false}
                              isItemPdf={isViewingItemPdf}
                            />
                            <div className="pdf-source-viewer__quote mt-3 space-y-2">
                              <strong>Cited:</strong>
                              <ul className="list-disc list-inside space-y-1 text-sm text-neutral-700 dark:text-neutral-300">
                                {pulse.evidence.map((ev, idx) => (
                                  <li key={idx}>
                                    <blockquote className="inline">{ev.quote}</blockquote>
                                  </li>
                                ))}
                              </ul>
                              {!anyHighlightable && pulse.evidence.some((ev) => ev.quote) && (
                                <p className="pdf-source-viewer__warning text-amber-600 dark:text-amber-400 text-sm mt-2">
                                  {isViewingItemPdf
                                    ? "Citation shown above; highlights are for the full agenda when viewing the full document."
                                    : "Highlight unavailable — quote could not be matched to exact location."}
                                </p>
                              )}
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      (() => {
                        const pageNum = drawerPdfPage ?? 1;
                        const dim = pagesData.find((p) => p.page_number === pageNum);
                        const totalPages = numPdfPages > 0 ? numPdfPages : Math.max(1, pagesData.length);
                        return (
                          <PdfSourceViewer
                            pdfUrl={pdfBlobUrl}
                            pageNumber={pageNum}
                            pageWidthPts={dim?.page_width_pts ?? 612}
                            pageHeightPts={dim?.page_height_pts ?? 792}
                            rects={[]}
                            quote=""
                            highlightable={false}
                            numPages={totalPages}
                            onDocumentLoadSuccess={({ numPages }) => setNumPdfPages(numPages)}
                            onPageChange={(p) => setDrawerPdfPage(p)}
                            showPageControls={false}
                            isItemPdf={isViewingItemPdf}
                          />
                        );
                      })()
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Unpin Confirmation Dialog */}
    <AlertDialog open={showUnpinConfirm} onOpenChange={setShowUnpinConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove from Pinned Pulses?</AlertDialogTitle>
          <AlertDialogDescription>
            Once unpinned, this pulse will be removed from your dashboard entirely and cannot be recovered. 
            You won't be able to see this pulse again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep Pinned</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (pulse?.insightId != null && onUnpin) {
                onUnpin(pulse.insightId);
              } else {
                onTogglePin();
              }
              setShowUnpinConfirm(false);
              onClose();
            }}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Unpin & Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Mark correction dialog — explain why this shouldn't have been a pulse (for model training) */}
    <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark correction</DialogTitle>
          <DialogDescription>
            Explain why this shouldn&apos;t have been marked as a pulse. Your feedback will be used to train the model so it learns what should and shouldn&apos;t be an insight.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="e.g. This is a routine administrative item with no material change to policy or process."
          value={correctionExplanation}
          onChange={(e) => setCorrectionExplanation(e.target.value)}
          className="min-h-[120px] resize-y"
          disabled={correctionSubmitting}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => {
              setShowCorrectionDialog(false);
              setCorrectionExplanation("");
            }}
            disabled={correctionSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCorrection}
            disabled={!correctionExplanation.trim() || correctionSubmitting}
          >
            {correctionSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting…
              </>
            ) : (
              "Submit correction"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
